import { useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api.js';
import { DataTable, type Column } from '../../components/DataTable.js';

/**
 * The audit trail. ADMIN only.
 *
 * Fetched with a plain `api()` call rather than Refine's `useList`, because this is
 * not a resource: there is no create, no edit, no delete, and there never will be —
 * `app_user` holds no UPDATE or DELETE privilege on `audit_logs` at all (invariant
 * 3). Modelling it as a Refine resource would give it a query cache and a mutation
 * surface for operations the database refuses.
 *
 * A non-ADMIN who reaches this URL gets a 403 from `audit:read` and is told so. The
 * page does not hide itself, because the honest failure is the more useful one — and
 * because hiding it would be the only thing standing there if the guard were ever
 * removed.
 */
interface AuditEntry {
  id: string;
  action: string;
  entityTable: string;
  entityId: string | null;
  actorEmail: string | null;
  actorType: string;
  actorIp: string | null;
  summary: string | null;
  occurredAt: string;
}

const columns: Column<AuditEntry>[] = [
  {
    key: 'when',
    header: 'When',
    // Local time, because the person reading this is in Michigan and comparing it
    // against a phone call. The stored value is UTC.
    render: (row) => new Date(row.occurredAt).toLocaleString(),
  },
  {
    key: 'actor',
    header: 'Who',
    render: (row) => (
      <>
        <strong>{row.actorEmail ?? 'system'}</strong>
        <div className="muted">
          {row.actorType}
          {row.actorIp ? ` · ${row.actorIp}` : ''}
        </div>
      </>
    ),
  },
  {
    key: 'action',
    header: 'Action',
    render: (row) => (
      <>
        <span className="pill">{row.action}</span> <code className="muted">{row.entityTable}</code>
      </>
    ),
  },
  { key: 'summary', header: 'Summary', render: (row) => row.summary ?? '—' },
];

export function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await api<{ entries: AuditEntry[] }>('/staff/audit-log');
        if (!cancelled) setEntries(response.entries);
      } catch (caught) {
        if (!cancelled) setError(caught);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const forbidden = error instanceof ApiError && error.status === 403;

  return (
    <section className="stack">
      <h1>Audit log</h1>
      <p className="muted">The fifty most recent entries.</p>

      {forbidden ? (
        <p className="alert alert--warn">
          {error instanceof ApiError ? error.message : ''} This page exists to be refused honestly
          rather than hidden.
        </p>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={entries ?? []}
            rowKey={(row) => row.id}
            isLoading={entries === null && !error}
            error={forbidden ? null : error}
            emptyMessage="No entries yet. If staff have been making changes, the audit middleware is not writing — that is a compliance problem, not an empty list."
          />
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            Reads are not logged in general — logging every page load buries the entries that
            matter. Opening a sensitive record is the exception, and appears here as a READ against{' '}
            <code>tenant_sensitive</code> or <code>owner_sensitive</code>. Field names are recorded
            on writes to those tables; values never are.
          </p>
        </>
      )}
    </section>
  );
}
